import { Controller, useFormContext } from 'react-hook-form'
import { useDropzone } from 'react-dropzone'
import { FC, useState } from 'react'
import { UploadIcon } from 'lucide-react'
import { formatBytes } from '../../utils'
import { BsFileEarmarkPdfFill, BsFillFileEarmarkWordFill } from 'react-icons/bs'

export const DropzoneField: FC<{
  name: string
  multiple?: boolean
  onFilesAccepted?: () => void
}> = ({ name, multiple, onFilesAccepted, ...rest }) => {
  const { control } = useFormContext()

  return (
    <Controller
      render={({ field: { onChange } }) => (
        <Dropzone
          multiple={multiple}
          onFileChange={files => onChange(files)}
          onFilesAccepted={onFilesAccepted ?? (() => {})}
          {...rest}
        />
      )}
      name={name}
      control={control}
      defaultValue=""
    />
  )
}

const Dropzone: FC<{
  multiple?: boolean
  onFileChange: (files: any) => void
  onFilesAccepted: () => void
}> = ({ multiple, onFileChange, onFilesAccepted, ...rest }) => {
  const [selectedFiles, setSelectedFiles] = useState<any>([])

  function handleAcceptedFiles(files: any) {
    files.map((file: any) =>
      Object.assign(file, {
        formattedSize: formatBytes(file.size, 0),
      })
    )
    setSelectedFiles(files)
    onFileChange(multiple ? files : files[0])
    onFilesAccepted()
  }

  const { getRootProps, getInputProps } = useDropzone({
    multiple,

    accept: {
      'application/pdf': ['.pdf'],
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document': ['.docx'],
    },
    onDrop: handleAcceptedFiles,
    ...rest,
  })

  return (
    <>
      <div
        {...getRootProps({
          className:
            'w-full max-w-md border-2 border-dashed border-gray-300 dark:border-gray-600 rounded-md p-6 flex flex-col items-center justify-center space-y-2 cursor-pointer hover:border-gray-400 dark:hover:border-gray-500 transition-colors',
        })}
      >
        <input {...getInputProps()} className="dropzone-input" />
        <UploadIcon className="h-8 w-8 text-gray-500 dark:text-gray-400" />
        <p className="text-center text-sm text-gray-500 dark:text-gray-400">
          Drag and drop your file here or click to upload.
          <br />
          Accepted file types: DOCX, PDF
        </p>
      </div>
      <div className="dropzone-previews mt-3" id="file-previews">
        {selectedFiles.map((f: any, i: any) => {
          return (
            <div className="w-full max-w-md space-y-2" key={i}>
              <div className="flex items-center justify-between rounded-md px-4 py-4">
                <div className="flex items-center gap-4">
                  {f.type === 'application/pdf' ? (
                    <BsFileEarmarkPdfFill className="h-10 w-10 text-red-500 dark:text-red-400" />
                  ) : (
                    <BsFillFileEarmarkWordFill className="h-10 w-10 text-blue-500 dark:text-blue-400" />
                  )}
                  <div>
                    <p className="font-medium">{f.name}</p>
                    <p className="text-sm text-gray-500 dark:text-gray-400">{f.formattedSize}</p>
                  </div>
                </div>
              </div>
            </div>
          )
        })}
      </div>
    </>
  )
}
